import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatPeriodEnd, isProActive, fetchUsage } from '../billing';
import PageMeta from '../PageMeta';

function riskCounts(analysis) {
  const counts = { high: 0, medium: 0, low: 0 };
  for (const r of analysis?.risks || []) {
    if (counts[r.tier] !== undefined) counts[r.tier] += 1;
  }
  return counts;
}

/* Account panel: signed-in Pro subscribers see their email, plan and this
   month's scan count (synced across devices via the server); everyone else
   gets the email sign-in form. Also used by the /signin page. */
export function AccountCard({ plan, onCancelPlan, onSignIn, onSignOut }) {
  const proActive = isProActive(plan);
  const endsOn = plan?.cancelAtPeriodEnd ? formatPeriodEnd(plan.periodEnd) : null;
  const [usage, setUsage] = useState(null);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // { ok, message }
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (proActive && plan.type === 'pro-monthly') {
      fetchUsage(plan).then(setUsage);
    } else {
      setUsage(null);
    }
  }, [proActive, plan?.token, plan?.type]);

  async function submit(e) {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setStatus(null);
    try {
      const message = await onSignIn(email.trim());
      setStatus({ ok: true, message });
    } catch (err) {
      setStatus({ ok: false, message: err.message || 'Sign-in failed. Please try again.' });
    } finally {
      setSending(false);
    }
  }

  if (!proActive) {
    return (
      <section className="account-card" aria-label="Sign in">
        <div className="account-signin-copy">
          <h2>Have Prop Doc Pro?</h2>
          <p>
            Sign in with the email you used at checkout to use your subscription on
            this device — your plan and monthly report count follow your account.
          </p>
        </div>
        <form className="account-signin-form" onSubmit={submit}>
          <input
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={sending}
            aria-label="Email address"
          />
          <button type="submit" className="btn-cta-primary" disabled={sending}>
            {sending ? 'Sending…' : 'Email me a sign-in link'}
          </button>
        </form>
        {status && (
          <p className={`account-status ${status.ok ? 'account-status-ok' : 'account-status-err'}`} role="status">
            {status.message}
          </p>
        )}
      </section>
    );
  }

  const planLabel = endsOn
    ? `Pro — active until ${endsOn}`
    : plan.type === 'pro-annual'
      ? 'Pro annual — 200+ reports a year'
      : 'Pro monthly';
  const pct = usage?.metered ? Math.min(100, Math.round((usage.used / usage.limit) * 100)) : 0;

  return (
    <section className="account-card" aria-label="Your account">
      <div className="account-row">
        <div className="account-id">
          <span className="account-avatar" aria-hidden="true">
            {(plan.email || 'P')[0].toUpperCase()}
          </span>
          <div>
            <span className="account-email">{plan.email || 'Pro subscriber'}</span>
            <span className="account-plan">
              <span className="site-nav-pro-dot" /> {planLabel}
            </span>
          </div>
        </div>
        <div className="account-actions">
          {!endsOn && (
            <button className="account-link" onClick={onCancelPlan}>Cancel plan</button>
          )}
          <button className="account-link" onClick={onSignOut}>Sign out</button>
        </div>
      </div>
      {usage?.metered && (
        <div className="account-usage">
          <div className="account-usage-bar" role="progressbar" aria-valuenow={usage.used} aria-valuemin={0} aria-valuemax={usage.limit}>
            <span style={{ width: `${pct}%` }} />
          </div>
          <span className="account-usage-label">
            <strong>{usage.used}</strong> of {usage.limit} reports used this month
          </span>
        </div>
      )}
    </section>
  );
}

export default function History({ history, onLoad, onClear, plan, onCancelPlan, onSignIn, onSignOut }) {
  return (
    <main className="history-page">
      <PageMeta title="Your reports — Prop Doc" path="/history" noindex />
      <section className="history-head">
        <div>
          <h1>My reports</h1>
          <p>
            Reports are stored only in this browser — nothing is kept on our servers.
            The most recent 15 are listed here.
          </p>
        </div>
        {history.length > 0 && (
          <button className="history-clear" onClick={onClear}>Clear all</button>
        )}
      </section>

      <AccountCard
        plan={plan}
        onCancelPlan={onCancelPlan}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
      />

      {history.length === 0 ? (
        <div className="history-empty">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <h2>No reports yet</h2>
          <p>Analyse your first Section 32/Contract of Sale and it will appear here, ready to reopen anytime.</p>
          <Link to="/analyze" className="btn-cta-primary">Analyse a document</Link>
        </div>
      ) : (
        <ul className="history-grid">
          {history.map(entry => {
            const analysis = entry.data.analysis;
            const address = analysis?.propertySummary?.fullAddress || entry.data.filename;
            const type = analysis?.propertySummary?.propertyType;
            const counts = riskCounts(analysis);
            const fees = (analysis?.fees || []).length;
            const date = new Date(entry.date).toLocaleDateString('en-AU', {
              day: 'numeric', month: 'short', year: 'numeric',
            });
            return (
              <li key={entry.id}>
                <button className="history-card" onClick={() => onLoad(entry.data)}>
                  <div className="history-card-address">{address}</div>
                  <div className="history-card-meta">
                    {type && <span className="history-card-type">{type}</span>}
                    <span>{date}</span>
                  </div>
                  <div className="history-card-chips">
                    {counts.high > 0 && <span className="hero-mock-chip hero-mock-chip-high">{counts.high} high</span>}
                    {counts.medium > 0 && <span className="hero-mock-chip hero-mock-chip-med">{counts.medium} medium</span>}
                    {counts.low > 0 && <span className="hero-mock-chip hero-mock-chip-low">{counts.low} low</span>}
                    {fees > 0 && <span className="hero-mock-chip history-chip-fees">{fees} fee {fees === 1 ? 'source' : 'sources'}</span>}
                  </div>
                  <span className="history-card-open">
                    Open report
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
